/**
 * FreeModeGrid — open-table tile grid shown inside a mode=free room.
 * Any player can tap a tile to launch a game; server validates against
 * `allowedGames` and the lobby state.
 */
import type { LobbyView } from '@tt/lobby';
import type { GameTile } from '../../lib/api';
import { GameThumb } from './GameThumb';
import { gameAvailability, livePlayerCount, playerReqLabel } from './gameAvailability';

interface Props {
  lobby: LobbyView;
  games: GameTile[];
  onLaunch: (gameId: string) => void;
}

export function FreeModeGrid({ lobby, games, onLaunch }: Props) {
  const allowed = lobby.settings.allowedGames;
  // R1#17: when allowedGames stores the BASE game id (e.g. 'tap-race'),
  // we still want to show separate-variant tiles whose baseGameId matches.
  // Mirrors PostRoundPane.tsx's HostPickSection predicate.
  const filtered = games.filter((g) =>
    !allowed.length
    || allowed.includes(g.id)
    || (g.baseGameId ? allowed.includes(g.baseGameId) : false),
  );
  const playerCount = livePlayerCount(lobby);
  return (
    <section className="free-grid-wrap">
      <h2 className="muted home-tiles-title">Open table — pick anything</h2>
      <div className="tile-grid">
        {filtered.map((g) => {
          const availability = gameAvailability(g, playerCount);
          return (
            <button
              key={g.id}
              type="button"
              className={`tile-card${availability.playable ? '' : ' disabled'}`}
              onClick={() => { if (availability.playable) onLaunch(g.id); }}
              aria-label={availability.playable ? `launch ${g.name}` : `${g.name} unavailable: ${availability.reason}`}
              disabled={!availability.playable}
            >
              <GameThumb gameId={g.id} category={g.category} name={g.name} chaoticness={g.chaoticness} />
              <div className="tile-name">{g.name}</div>
              <div className="tile-tag muted">{availability.reason ?? g.pitch}</div>
              <div className="tile-meta muted">{playerReqLabel(g)}</div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
